import { LoadingDots, PackageSelector, Pill } from '@flos/react-ui';
import type { PackageProps } from '@flos/react-ui';
import type { Coverage } from '@flos/react-ui/build/collections/package-selector/coverage-label';
import type { CoveragePackageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import _ from 'lodash';
import { useHippoText } from '../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { hippoCoverageTextsSelector } from '../../../recoil/selectors/hippoCoverageTextsSelector';
import CoveragePackageModalLink from './CoveragePackageModalLink';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCoverable } from '../../../utils/coverablesUtil';
import {
    getCurrencyString,
    paymentFrequencyMap,
    periodicityUnit
} from '../../../utils/currencyUtil';
import { numberOfFlavourCoverablesSelector } from '../../../recoil/selectors/numberOfFlavourCoverablesSelector';
import { giftCertificateAtom } from '../../../recoil/atoms/giftCertificateAtom';
import { useAsyncRecoilValue } from '../../../hooks/useAsyncRecoilValue';
import { COVERABLE_CODE } from '../../../constants/coverable';
import { isGiftCardEligibleSelector } from '../../../recoil/selectors/isGiftCardEligibleSelector';

interface CoveragePackagesData {
    packages: PackageProps[];
    uniqueCoverages: Coverage[];
}

const getPackageSelectorData = (
    packages: CoveragePackageDTO[],
    defaultPackage: number,
    flavourName: string
): CoveragePackagesData => {
    const uniqueCoveragesForPackage: Coverage[] = [];
    const sortedPackages = [...packages].sort((a, b) => a.coverages.length - b.coverages.length);
    sortedPackages.forEach((pack) => {
        pack.coverages.forEach((cov) => {
            uniqueCoveragesForPackage.push({
                name: cov.coverageName,
                id: cov.coverageId
            });
        });
    });

    return {
        packages: packages.map((pack, index) => {
            const priceAsString: string = getCurrencyString(
                pack.price?.monthlyPrice?.amount ?? 0,
                ' kr.' + periodicityUnit[paymentFrequencyMap.monthly],
                2
            );
            const showRecommendationBadge =
                defaultPackage === index && flavourName === 'Børneforsikring';

            return {
                name: pack.name,
                price: priceAsString,
                coveredCoverageIds: pack.coverages.map((coverage) => coverage.coverageId),
                helpBlock: showRecommendationBadge && <Pill content="Passer til dine valg" />
            };
        }),
        uniqueCoverages: _.uniqBy(uniqueCoveragesForPackage, 'id').reverse()
    };
};

const CoveragePackageCards = ({
    packages,
    defaultPackage,
    isLoading,
    setSelectedPackageIndex
}: {
    packages: CoveragePackageDTO[];
    defaultPackage: number;
    isLoading: boolean;
    setSelectedPackageIndex: React.Dispatch<React.SetStateAction<number>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const modalName = getHippoText('DSS-COVERAGE-PACKAGE-PAGE-INFO');
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const isGiftCardEligible = useRecoilValue(isGiftCardEligibleSelector);
    const { flavourName = '', fixedId = '', flavourCode } = currentProduct;
    const giftCertificates = useRecoilValue(giftCertificateAtom);
    const numberOfFlavourCoverables = useRecoilValue(
        numberOfFlavourCoverablesSelector(flavourName)
    );
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId);
    const coveragePackageData = getPackageSelectorData(packages, defaultPackage, flavourName);
    const [onsaCoverageTexts, areTextsLoading] = useAsyncRecoilValue(
        hippoCoverageTextsSelector({
            coverageIds: coveragePackageData.uniqueCoverages.flatMap((cov) => cov.id),
            termIds: coverable.termIds
        })
    );

    const uniqueCoverages = coveragePackageData.uniqueCoverages.map((coverage) => {
        const coverageText = onsaCoverageTexts?.find(
            (coverageText) => coverageText.coverageItemId === coverage.id
        );
        const coverageDescription =
            coverageText?.nonCustomerDescription !== ''
                ? coverageText?.nonCustomerDescription
                : coverageText?.subtitle;
        return {
            ...coverage,
            info: areTextsLoading ? (
                <LoadingDots centered={false} />
            ) : (
                <CoveragePackageModalLink
                    modalName={modalName}
                    title={coverage.name?.toString() ?? ''}
                    description={coverageDescription ?? ''}
                    included={coverageText?.included?.items ?? []}
                    excluded={coverageText?.excluded?.items ?? []}
                />
            )
        };
    });

    const giftCertificateValue = giftCertificates[flavourCode];

    if (
        isGiftCardEligible &&
        coverable.isAddedInOnsa &&
        giftCertificateValue > 0 &&
        numberOfFlavourCoverables < 5
    ) {
        const giftCardText = getHippoText('DSS-COVERAGE-PACKAGE-PAGE-GIFT-CARD', {
            value: giftCertificateValue
        });
        const modalText =
            coverable.flavourCode === COVERABLE_CODE.accident
                ? 'DSS-GIFT-MODAL-DESCRIPTION-ACCIDENT'
                : 'DSS-GIFT-MODAL-DESCRIPTION';
        uniqueCoverages.push({
            id: 'giftcert',
            name: giftCardText,
            gift: true,
            info: (
                <CoveragePackageModalLink
                    modalName={modalName}
                    title={getHippoText('DSS-GIFT-MODAL-TITLE')}
                    description={`<p>${getHippoText(modalText, {
                        value: giftCertificateValue,
                        flavour: flavourName.toLowerCase()
                    })}</p>
                        <p>${getHippoText('DSS-GIFT-MODAL-DESCRIPTION-TWO')}</p>`}
                />
            )
        });
        coveragePackageData.packages.forEach((covPackage) => {
            covPackage.coveredCoverageIds.push('giftcert');
        });
    }

    if (!currentProduct) {
        return <></>;
    }

    return (
        <PackageSelector
            defaultSelectedIndex={defaultPackage}
            onChangeHandler={(index) => {
                setSelectedPackageIndex(Number(index));
            }}
            packages={coveragePackageData.packages}
            uniqueCoverageList={uniqueCoverages}
            loadingPrice={isLoading}
        />
    );
};

export default CoveragePackageCards;
